import axios from "axios";
import { getAuth } from "firebase/auth";
import getFirebase, { getDbInterfaceAuth } from "../../firebase";
import { AUTHENTICATION_SCOPES, classifyApiFailure, resolveApiTarget } from "./apiTarget";

export * from "./apiTarget";

// localStorage key holding a manually-pasted Firebase ID token used in dev mode
// to bypass the Google login flow (see login.jsx). Only honored in development.
export const DEV_TOKEN_KEY = "devToken";

export const isDevMode = () => process.env.NODE_ENV === "development";

export const getDevToken = () =>
  isDevMode() ? localStorage.getItem(DEV_TOKEN_KEY) : null;

// A dedicated axios instance. The previous implementation mutated axios.defaults, which
// made every later axios call in the app (Prismic, ipapi.co) carry the Firebase ID token.
const API_REQUEST_TIMEOUT_MILLISECONDS = 30000;
const apiHttpClient = axios.create({ timeout: API_REQUEST_TIMEOUT_MILLISECONDS });

// Anyone (the DB Interface session gate) can listen to classified API failures, e.g. to
// sign the user out on DB_INTERFACE_SESSION_EXPIRED.
const apiFailureListeners = new Set();
export function subscribeToApiFailures(listener) {
  apiFailureListeners.add(listener);
  return () => apiFailureListeners.delete(listener);
}

apiHttpClient.interceptors.response.use(undefined, (error) => {
  const apiFailure = classifyApiFailure(error);
  error.apiFailure = apiFailure;
  apiFailureListeners.forEach((listener) => {
    try {
      listener(apiFailure);
    } catch (listenerError) {
      console.error("[commHelper] api failure listener threw:", listenerError);
    }
  });
  return Promise.reject(error);
});

// --- DB Interface step-up (TOTP) token, phase 3. Per tab, like the session itself. ---
const STEP_UP_TOKEN_KEY = "dbInterfaceStepUpToken";
const STEP_UP_TOKEN_EXPIRES_AT_KEY = "dbInterfaceStepUpTokenExpiresAt";
let stepUpTokenInMemory = null;
let stepUpTokenExpiresAtInMemory = 0;

export function setStepUpToken(token, expiresAtMilliseconds) {
  stepUpTokenInMemory = token;
  stepUpTokenExpiresAtInMemory = expiresAtMilliseconds;
  try {
    sessionStorage.setItem(STEP_UP_TOKEN_KEY, token);
    sessionStorage.setItem(STEP_UP_TOKEN_EXPIRES_AT_KEY, String(expiresAtMilliseconds));
  } catch (e) {
    // sessionStorage unavailable: the in-memory copy still serves this page load.
  }
}

export function clearStepUpToken() {
  stepUpTokenInMemory = null;
  stepUpTokenExpiresAtInMemory = 0;
  try {
    sessionStorage.removeItem(STEP_UP_TOKEN_KEY);
    sessionStorage.removeItem(STEP_UP_TOKEN_EXPIRES_AT_KEY);
  } catch (e) {
    // ignore
  }
}

export function getStepUpToken() {
  if (!stepUpTokenInMemory) {
    try {
      stepUpTokenInMemory = sessionStorage.getItem(STEP_UP_TOKEN_KEY);
      stepUpTokenExpiresAtInMemory = Number(sessionStorage.getItem(STEP_UP_TOKEN_EXPIRES_AT_KEY)) || 0;
    } catch (e) {
      return null;
    }
  }
  if (!stepUpTokenInMemory) return null;
  if (stepUpTokenExpiresAtInMemory && Date.now() >= stepUpTokenExpiresAtInMemory) {
    clearStepUpToken();
    return null;
  }
  return stepUpTokenInMemory;
}

// Resolve the auth token for API calls. In development, prefer a manually provided dev
// token; otherwise use the Firebase user of the target's authentication scope.
async function resolveToken(apiTarget) {
  const devToken = getDevToken();
  if (devToken) return devToken;
  const auth = apiTarget.authenticationScope === AUTHENTICATION_SCOPES.dbInterface
    ? getDbInterfaceAuth()
    : getAuth(getFirebase());
  // <Login> may render children off a cached user before Firebase Auth has finished
  // restoring its session — currentUser is still null at that instant, and calling
  // .getIdToken() on it throws. Wait for the SDK's initial auth state to settle first.
  await auth.authStateReady();
  if (!auth.currentUser) {
    throw new Error("Not authenticated");
  }
  return auth.currentUser.getIdToken();
}

async function buildRequestConfig(apiTarget) {
  const headers = { token: await resolveToken(apiTarget) };
  if (apiTarget.authenticationScope === AUTHENTICATION_SCOPES.dbInterface) {
    const stepUpToken = getStepUpToken();
    if (stepUpToken) headers["x-dbinterface-step-up"] = stepUpToken;
  }
  // `apiTarget` rides along on the request config so the response interceptor can tell
  // which host / mode a failure belongs to (axios keeps unknown config keys).
  return { baseURL: apiTarget.baseUrl, headers, apiTarget };
}

// `isDevOrApiTarget`: legacy boolean (public host, default auth — Printer dashboard) or an
// apiTarget object from createApiTarget(...) (DB Interface).
export async function getPost(url, data, isDevOrApiTarget = false) {
  const apiTarget = resolveApiTarget(isDevOrApiTarget);
  return await apiHttpClient.post(url, data, await buildRequestConfig(apiTarget));
}

export async function getRequest(url, isDevOrApiTarget = false) {
  const apiTarget = resolveApiTarget(isDevOrApiTarget);
  return await apiHttpClient.get(url, await buildRequestConfig(apiTarget));
}
